import '../css/GroupPage.less';
import '../css/ProjectPage.less';

import NoMatch from '../components/NoMatch';
import * as React from 'react';
import {Header, Loader, Segment} from 'semantic-ui-react';
import {Redirect} from 'react-router-dom';
import {useArtifactTypeQuery} from '../state/graphql/artifactTypeQuery';
import ArtifactTypeSidebar from './ArtifactTypeSidebar';
import * as Urls from '../util/urls';
import {useArtifactCollectionQuery} from '../generated/graphql';
import makeComp from '../util/profiler';
import Artifact from './Artifact';

const ArtifactsView = makeComp<{
  entityName: string;
  projectName: string;
  artifactTypeName: string;
  artifactCollectionName: string;
  artifactCommitHash?: string;
  artifactTab?: string;
  filePath?: string;
  history: any;
}>(
  props => {
    const {
      entityName,
      projectName,
      artifactTypeName,
      artifactCollectionName,
      artifactTab,
      artifactCommitHash,
      filePath,
      history,
    } = props;

    const q2 = useArtifactCollectionQuery({
      variables: {
        entityName,
        projectName,
        artifactTypeName,
        artifactCollectionName,
      },
    });

    if (q2.loading) {
      return <Loader />;
    }

    const artifactCollection = q2.data?.project?.artifactType?.artifactSequence;
    const artifacts = artifactCollection?.artifacts.edges.map(n => n.node);
    if (artifacts == null || artifacts?.length === 0) {
      return <Segment>No artifacts have been created yet.</Segment>;
    }

    const firstArtifact = artifacts[0];
    if (firstArtifact.commitHash == null) {
      return <Segment>No commit hashes have been created yet.</Segment>;
    }

    if (artifactCommitHash == null) {
      const url = Urls.artifactSubpage({
        entityName,
        projectName,
        artifactTypeName,
        artifactSequenceName: artifactCollectionName,
        artifactCommitHash: firstArtifact.commitHash,
      });
      return <Redirect to={url} />;
    }

    return (
      <Artifact
        entityName={entityName}
        projectName={projectName}
        artifactTypeName={artifactTypeName}
        artifactSequenceName={artifactCollectionName!}
        artifactCommitHash={artifactCommitHash ?? firstArtifact.commitHash}
        artifactTab={artifactTab}
        filePath={filePath}
        history={history}
        refetch={() => q2.refetch()}
      />
    );
  },
  {id: 'ArtifactsView'}
);

interface ArtifactProps {
  entityName: string;
  projectName: string;
  artifactTypeName: string;
  artifactCollectionName?: string;
  artifactCommitHash?: string;
  artifactTab?: string;
  filePath?: string;
  history: any;
}

const ArtifactType = makeComp(
  (props: ArtifactProps) => {
    const {
      entityName,
      projectName,
      artifactTypeName,
      history,
      artifactCollectionName,
    } = props;
    const q = useArtifactTypeQuery({entityName, projectName, artifactTypeName});

    if (q.loading) {
      return <Loader />;
    }

    const artifactType = q.project.artifactType;
    if (artifactType == null) {
      return <NoMatch />;
    }

    const artifactSequences = artifactType.artifactSequences.edges;
    if (artifactSequences.length === 0) {
      return <NoMatch />;
    }

    const firstArtifactSequence = artifactSequences[0];
    if (artifactCollectionName == null) {
      const url = Urls.artifactSequence({
        entityName,
        projectName,
        artifactTypeName,
        artifactSequenceName: firstArtifactSequence.node.name,
      });
      return <Redirect to={url} />;
    }

    return (
      <div className="artifact-page">
        <div className="artifact-page-side">
          <Header style={{clear: 'both'}} className="artifacts-page-header">
            Type: {artifactTypeName}
          </Header>
          {artifactSequences.length === 0 ? (
            <span>No artifacts have been created yet.</span>
          ) : (
            <>
              <ArtifactTypeSidebar
                {...props}
                artifactCollectionName={artifactCollectionName}
                artifactSequences={artifactSequences.map(a => a.node)}
              />
            </>
          )}
        </div>
        <div className="artifact-page-main">
          <ArtifactsView
            {...props}
            artifactCollectionName={artifactCollectionName}
            history={history}
          />
        </div>
      </div>
    );
  },
  {id: 'ArtifactType'}
);

export default ArtifactType;
